import { CopyIcon, InfoCircledIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Box, Flex, Grid, Text } from '@radix-ui/themes';
import { AimingContextHelper } from 'classes/helpers/aiming-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTooltip } from 'components/common/tooltip';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  IBallState,
  IClosedLoopBuildChars,
  IPitch,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { ISpin } from 'lib_ts/interfaces/pitches/i-base';
import { clone, first, merge } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PitchListsService } from 'services/pitch-lists.service';
import { PitchesService } from 'services/pitches.service';
import { StateTransformService } from 'services/state-transform.service';
import useListLibraryStore from 'stores/use-list-library-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const COMPONENT_NAME = 'EditSpinsDialog';

// how many decimals to print
const PRECISION = 0;

// show warning if any axis requested differs from corresponding avgs by more than this
const WARNING_THRESHOLD_RPM = 500;

interface IProps {
  pitch: IPitch;
  onClose: () => void;

  // hides the update button
  readonly: boolean;
}

interface IState {
  actual_wx_text?: string;
  actual_wx_rpm: number;
  actual_wy_text?: string;
  actual_wy_rpm: number;
  actual_wz_text?: string;
  actual_wz_rpm: number;

  target_wx_text?: string;
  target_wx_rpm: number;
  target_wy_text?: string;
  target_wy_rpm: number;
  target_wz_text?: string;
  target_wz_rpm: number;

  overriding: boolean;
}

export const EditSpinsDialog = (props: IProps) => {
  const { machine } = useContext(MachineContext);

  const activeList = useListLibraryStore((s) => s.activeList);
  const loading = usePitchListStore((s) => s.loading);

  const trainingShots = useTrainedMsStore(
    (s) => s.trainingShotsDict[props.pitch.bs_hash] ?? []
  );

  // load training shots
  useEffect(() => {
    useTrainedMsStore.getState().lazyLoadShots(props.pitch.bs_hash, false);
  }, [props.pitch.bs_hash]);

  const medians = useMemo(() => {
    if (trainingShots.length === 0) {
      return undefined;
    }

    const bss = trainingShots
      .filter((s) => !!s.bs)
      .map((s) => s.bs as IBallState);
    const breaks = trainingShots.map((s) => TrajHelper.getBreaksFromShot(s));
    const trajs = trainingShots
      .filter((s) => !!s.traj)
      .map((s) => s.traj as ITrajectory);

    return {
      bs:
        bss.length > 0
          ? (MiscHelper.getMedianObject(bss) as IBallState)
          : undefined,
      break:
        breaks.length > 0
          ? (MiscHelper.getMedianObject(breaks) as ITrajektRefBreak)
          : undefined,
      traj:
        trajs.length > 0
          ? (MiscHelper.getMedianObject(trajs) as ITrajectory)
          : undefined,
    };
  }, [trainingShots]);

  const [state, setState] = useState<IState>({
    actual_wx_rpm: 0,
    actual_wy_rpm: 0,
    actual_wz_rpm: 0,

    target_wx_rpm: props.pitch.bs?.wx ?? 0,
    target_wy_rpm: props.pitch.bs?.wy ?? 0,
    target_wz_rpm: props.pitch.bs?.wz ?? 0,

    overriding: false,
  });

  useEffect(() => {
    setState((prev) => {
      if (!medians?.bs) {
        return prev;
      }

      const { pitch } = props;

      const target = pitch.bs;
      const useTarget = pitch.priority
        ? [BuildPriority.Default, BuildPriority.Spins].includes(pitch.priority)
        : true;

      const target_wx_rpm = useTarget ? target.wx : medians.bs.wx;
      const target_wy_rpm = useTarget ? target.wy : medians.bs.wy;
      const target_wz_rpm = useTarget ? target.wz : medians.bs.wz;

      const nextValue = clone(prev);

      nextValue.actual_wx_rpm = medians.bs.wx;
      nextValue.actual_wx_text = medians.bs.wx.toFixed(PRECISION);
      nextValue.actual_wy_rpm = medians.bs.wy;
      nextValue.actual_wy_text = medians.bs.wy.toFixed(PRECISION);
      nextValue.actual_wz_rpm = medians.bs.wz;
      nextValue.actual_wz_text = medians.bs.wz.toFixed(PRECISION);

      nextValue.target_wx_rpm = target_wx_rpm;
      nextValue.target_wx_text = target_wx_rpm.toFixed(PRECISION);
      nextValue.target_wy_rpm = target_wy_rpm;
      nextValue.target_wy_text = target_wy_rpm.toFixed(PRECISION);
      nextValue.target_wz_rpm = target_wz_rpm;
      nextValue.target_wz_text = target_wz_rpm.toFixed(PRECISION);

      return nextValue;
    });
  }, [props.pitch, medians]);

  const submit = useCallback(
    async (mode: 'copy' | 'update') => {
      if (mode === 'update' && props.readonly) {
        NotifyHelper.error({
          message_md: 'Updating is not allowed in read-only mode.',
        });
        return;
      }

      if (!medians?.bs) {
        NotifyHelper.error({
          message_md: 'There is insufficient data to proceed.',
        });
        return;
      }

      const tms = useTrainedMsStore.getState().getTms(props.pitch.bs_hash);

      if (!tms?.ms) {
        NotifyHelper.error({ message_md: 'There is no machine state.' });
        return;
      }

      const newSpin: ISpin = {
        wx: state.target_wx_rpm,
        wy: state.target_wy_rpm,
        wz: state.target_wz_rpm,
      };

      const newTargetBS: IBallState = clone(props.pitch.bs);
      merge(newTargetBS, newSpin);

      newTargetBS.wnet = BallHelper.getNetSpin(newSpin);

      const actualSpin: ISpin = {
        wx: state.actual_wx_rpm,
        wy: state.actual_wy_rpm,
        wz: state.actual_wz_rpm,
      };

      const actualBS: IBallState = clone(medians.bs);
      merge(actualBS, actualSpin);

      const chars: IClosedLoopBuildChars = {
        mongo_id: props.pitch._id,
        priority: BuildPriority.Spins,
        use_gradient: true,
        ms: tms.ms,
        traj: props.pitch.traj,
        seams: props.pitch.seams,
        target_bs: newTargetBS,
        actual_bs: actualBS,
      };

      const result = first(
        await StateTransformService.getInstance().buildManyClosedLoop({
          machineID: tms.machineID,
          pitches: [chars],
          stepSize: 1,
          notifyError: true,
        })
      );

      if (!result) {
        NotifyHelper.error({
          message_md: 'Failed to find adjusted pitch by ID.',
        });
        return;
      }

      const upsertedTms = await useTrainedMsStore.getState().upsertTms({
        machineID: tms.machineID,
        ball_type: tms.ball_type,
        bs_hash: MachineHelper.getBsHash(result.target_bs, chars.priority),
        ms: result.ms,
        shotIDs: [],
        contShotErrors: null,
        aimingTraj: result.traj,
        // * IMPORTANT: prevents PL from refreshing the ms before it can be trained
        skipRefresh: true,
        deleted: false,
      });

      if (!upsertedTms) {
        NotifyHelper.error({
          message_md: 'Failed to create optimized machine state.',
        });
        return;
      }

      const payload: Partial<IPitch> = clone(props.pitch);

      payload.bs = result.target_bs;
      payload.bs_hash = upsertedTms.bs_hash;
      payload.traj = result.traj;
      payload._mx_tms = upsertedTms;

      payload.seams = result.seams;
      payload.priority = BuildPriority.Spins;

      const aimed = AimingContextHelper.aimAdHoc({
        source: `${COMPONENT_NAME} > submit`,
        machine: machine,
        ms: upsertedTms.ms,
        pitch: payload as IPitch,
        plate: TrajHelper.getPlateLoc(props.pitch.traj),
        aimingShots: [],
      });

      if (!aimed) {
        NotifyHelper.error({ message_md: 'Failed to build new pitch.' });
        return;
      }

      aimed.pitch._original_id = props.pitch._id;

      // fix the prefix on the pitch name when copying (to disambiguate)
      if (mode === 'copy') {
        const nameSuffix = `(Spins Edited)`;
        aimed.pitch.name = `${props.pitch.name} ${nameSuffix}`;
      }

      // add the new pitch the list
      await PitchListsService.getInstance()
        .addPitches({
          listID: props.pitch._parent_id,
          data: [aimed.pitch],
        })
        .then((result) => {
          if (result.success) {
            return;
          }

          NotifyHelper.warning({
            message_md: result.error ?? 'Pitch could not be created.',
          });
        });

      // when updating, the new pitch has to replace the old version
      if (mode === 'update') {
        await PitchesService.getInstance().markAsDeleted([props.pitch._id]);
      }

      usePitchListStore.getState().reloadPitches({
        activeList: activeList,
      });

      props.onClose();
    },
    [
      props.readonly,
      props.pitch,
      props.onClose,
      trainingShots,
      medians,
      state.actual_wx_rpm,
      state.actual_wy_rpm,
      state.actual_wz_rpm,
      state.target_wx_rpm,
      state.target_wy_rpm,
      state.target_wz_rpm,
      activeList,
    ]
  );

  const renderContent = useCallback(() => {
    const diffX = state.actual_wx_rpm - state.target_wx_rpm;
    const warnX = Math.abs(diffX) > WARNING_THRESHOLD_RPM;

    const diffY = state.actual_wy_rpm - state.target_wy_rpm;
    const warnY = Math.abs(diffY) > WARNING_THRESHOLD_RPM;

    const diffZ = state.actual_wz_rpm - state.target_wz_rpm;
    const warnZ = Math.abs(diffZ) > WARNING_THRESHOLD_RPM;

    return (
      <Flex gap={RADIX.FLEX.GAP.LG} direction="column">
        <Text>
          Use this feature to directly control the 3D spin of a pitch. This
          feature will save a new pitch with the adjusted spin values.
        </Text>

        <Grid columns="3" gap={RADIX.FLEX.GAP.MD}>
          <Box></Box>
          <Box>
            {state.overriding ? 'User Override' : 'Actual'} &nbsp;
            <CommonTooltip
              trigger={<InfoCircledIcon />}
              text_md={
                state.overriding
                  ? 'Actual spins as input by user'
                  : 'Actual spins as reported by Rapsodo'
              }
            />
          </Box>
          <Box>Target</Box>
          <Box>Spin X (RPM)</Box>
          <Box>
            <CommonTextInput
              id="edit-spins-actual-wx"
              name="actual_wx_text"
              type="number"
              className="align-center"
              value={state.actual_wx_text}
              disabled={loading || !state.overriding}
              onOptionalNumericChange={(v) => {
                setState((prev) => ({
                  ...prev,
                  actual_wx_text: v?.toString(),
                  actual_wx_rpm: v ?? 0,
                }));
              }}
            />
          </Box>
          <Box>
            <CommonTextInput
              id="edit-spins-target-wx"
              name="target_wx_text"
              type="number"
              className="align-center"
              value={state.target_wx_text}
              disabled={loading}
              onOptionalNumericChange={(v) => {
                setState((prev) => ({
                  ...prev,
                  target_wx_text: v?.toString(),
                  target_wx_rpm: v ?? 0,
                }));
              }}
            />
          </Box>
          <Box>Spin Y (RPM)</Box>
          <Box>
            <CommonTextInput
              id="edit-spins-actual-wy"
              name="actual_wy_text"
              type="number"
              className="align-center"
              value={state.actual_wy_text}
              disabled={loading || !state.overriding}
              onOptionalNumericChange={(v) => {
                setState((prev) => ({
                  ...prev,
                  actual_wy_text: v?.toString(),
                  actual_wy_rpm: v ?? 0,
                }));
              }}
            />
          </Box>
          <Box>
            <CommonTextInput
              id="edit-spins-target-wy"
              name="target_wy_text"
              type="number"
              className="align-center"
              value={state.target_wy_text}
              disabled={loading}
              onOptionalNumericChange={(v) => {
                setState((prev) => ({
                  ...prev,
                  target_wy_text: v?.toString(),
                  target_wy_rpm: v ?? 0,
                }));
              }}
            />
          </Box>
          <Box>Spin Z (RPM)</Box>
          <Box>
            <CommonTextInput
              id="edit-spins-actual-wz"
              name="actual_wz_text"
              type="number"
              className="align-center"
              value={state.actual_wz_text}
              disabled={loading || !state.overriding}
              onOptionalNumericChange={(v) => {
                setState((prev) => ({
                  ...prev,
                  actual_wz_text: v?.toString(),
                  actual_wz_rpm: v ?? 0,
                }));
              }}
            />
          </Box>
          <Box>
            <CommonTextInput
              id="edit-spins-target-wz"
              name="target_wz_text"
              type="number"
              className="align-center"
              value={state.target_wz_text}
              disabled={loading}
              onOptionalNumericChange={(v) => {
                setState((prev) => ({
                  ...prev,
                  target_wz_text: v?.toString(),
                  target_wz_rpm: v ?? 0,
                }));
              }}
            />
          </Box>
        </Grid>

        {(warnX || warnY || warnZ) && (
          <CommonCallout
            text={(() => {
              const axesStrings = [];
              if (warnX) {
                axesStrings.push('X');
              }
              if (warnY) {
                axesStrings.push('Y');
              }
              if (warnZ) {
                axesStrings.push('Z');
              }
              const axesString = axesStrings.join(', ');
              return `Target value differs by more than ${WARNING_THRESHOLD_RPM} for ${
                axesStrings.length > 1 ? 'Axes' : 'Axis'
              } ${axesString}`;
            })()}
          />
        )}
      </Flex>
    );
  }, [
    loading,
    state.actual_wx_rpm,
    state.actual_wx_text,
    state.actual_wy_rpm,
    state.actual_wy_text,
    state.actual_wz_rpm,
    state.actual_wz_text,
    state.overriding,
    state.target_wx_rpm,
    state.target_wx_text,
    state.target_wy_rpm,
    state.target_wy_text,
    state.target_wz_rpm,
    state.target_wz_text,
  ]);

  const pitchName = props.pitch.name;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.LG}
        title={`${t('pl.edit-spins')} for "${pitchName}"`}
        content={renderContent()}
        buttons={[
          {
            label: `Override: ${state.overriding ? 'ON' : 'OFF'}`,
            color: state.overriding ? RADIX.COLOR.WARNING : undefined,
            onClick: () => {
              setState((prev) => {
                if (!medians?.bs) {
                  return prev;
                }

                const nextValue = clone(prev);

                // flip the toggle
                nextValue.overriding = !nextValue.overriding;

                // reset the values back to the median
                nextValue.actual_wx_rpm = medians.bs.wx;
                nextValue.actual_wx_text = medians.bs.wx.toFixed(PRECISION);
                nextValue.actual_wy_rpm = medians.bs.wy;
                nextValue.actual_wy_text = medians.bs.wy.toFixed(PRECISION);
                nextValue.actual_wz_rpm = medians.bs.wz;
                nextValue.actual_wz_text = medians.bs.wz.toFixed(PRECISION);

                return nextValue;
              });
            },
          },
          {
            icon: <CopyIcon />,
            label: 'common.copy',
            color: RADIX.COLOR.SUCCESS,
            variant: 'solid',
            disabled: trainingShots.length === 0,
            onClick: () => submit('copy'),
          },
          {
            invisible: props.readonly,
            icon: <UpdateIcon />,
            label: 'common.update',
            color: RADIX.COLOR.WARNING,
            disabled: trainingShots.length === 0,
            onClick: () => submit('update'),
          },
        ]}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
